/**
 * QuarterBenchmark — what is being asked against what is being paid, quarter
 * by quarter.
 *
 * The evidence strip answers "where does each comparable sit on the ruler".
 * This answers the other half: "and has that been moving?". Same pool, time
 * as the second axis.
 *
 * TWO SERIES THAT ARE NEVER MERGED
 * An asking price is a hope; a sold price is a fact. Averaged together they
 * produce a number that is neither, and the gap between them is the most
 * quotable figure on the page when it exists — it is the discount the market
 * is actually taking. So the two are bucketed and drawn separately, always,
 * and the page says the gap out loud when one period carries both.
 *
 * Source stays encoded by SHAPE, not colour, matching the evidence strip's
 * documented choice: filled = verified sale, hollow = asking. It survives
 * greyscale, colour blindness and a laptop angled across a table.
 *
 * BOXES, NOT CANDLESTICKS
 * A candle encodes open and close. A quarter of listings has neither — there
 * is no first or last trade, only a spread. Each period is drawn as a
 * quartile box: the middle half as the body, the full range as the whisker,
 * the median as the bar. It reads like a candle and claims only what exists.
 * Under `MIN_FOR_BOX` observations a period shows its individual points
 * instead, because quartiles over three listings are arithmetic without
 * meaning.
 *
 * Hand-rolled SVG (spec §9: no charting library, ≤ ~200 lines). RTL mirrors
 * the time axis; numerals stay Western.
 */

import type { SubjectComparable } from '@/types/marketPricing'
import { MIN_FOR_BOX, askToSoldGap, bucketComparables, periodsOf, type Bucket } from './benchmarkBuckets'
import { formatMoney } from './pricingIntelligence'
import { priceBriefT, type PriceBriefLocale } from './copy'
import { rulerGeometry, rulerX, type RulerDirection } from './priceScale'

const WIDTH = 600
const HEIGHT = 210
const PAD_X = 46
const TOP = 16
const BOTTOM = 158

interface QuarterBenchmarkProps {
  comparables: SubjectComparable[]
  currency: string
  dir: RulerDirection
  locale: PriceBriefLocale
  /** Injected in tests so bucketing is not clock-dependent. */
  now?: number
}

export function QuarterBenchmark({
  comparables,
  currency,
  dir,
  locale,
  now,
}: QuarterBenchmarkProps) {
  const t = (key: Parameters<typeof priceBriefT>[0], vars?: Record<string, string | number>) =>
    priceBriefT(key, locale, vars)

  const buckets = bucketComparables(comparables ?? [], now ?? Date.now())
  const periods = periodsOf(buckets)

  // Every comparable needs a date to be placed in time. When none has one
  // there is nothing to say, and an empty axis would imply a dead market.
  if (!periods.length) {
    return (
      <div
        data-testid="benchmark-empty"
        className="rounded-[var(--lc-radius-lg)] border border-dashed border-[var(--lc-border-strong)] bg-[var(--lc-surface-sunken)] px-[var(--lc-space-md)] py-[var(--lc-space-sm)]"
      >
        <p className="m-0 text-[length:var(--lc-type-body)] text-[var(--lc-text-muted)]">
          {t('benchmarkLabel')}
        </p>
        <p className="m-0 mt-[var(--lc-space-2xs)] text-[length:var(--lc-type-body-sm)] text-[var(--lc-text-muted)]">
          {t('benchmarkNoDates')}
        </p>
      </div>
    )
  }

  const all = buckets.flatMap((b) => [b.low, b.high])
  const min = Math.min(...all)
  const max = Math.max(...all)
  const pad = (max - min || max * 0.1) * 0.12
  const lo = Math.max(0, min - pad)
  const hi = max + pad

  const geom = rulerGeometry({ min: 0, max: Math.max(1, periods.length - 1) }, WIDTH, PAD_X)
  const slotX = (index: number) =>
    periods.length === 1 ? (geom.x0 + geom.x1) / 2 : rulerX(geom, index, dir)
  const y = (value: number) => BOTTOM - ((value - lo) / (hi - lo || 1)) * (BOTTOM - TOP)

  const slot = (geom.x1 - geom.x0) / Math.max(1, periods.length)
  const boxWidth = Math.min(26, Math.max(10, slot * 0.28))
  const gap = askToSoldGap(buckets)

  return (
    <figure data-testid="quarter-benchmark" className="m-0">
      <figcaption className="mb-[var(--lc-space-2xs)] text-[length:var(--lc-type-body-sm)] text-[var(--lc-text-secondary)]">
        {gap
          ? t('benchmarkGap', {
              percent: `${gap.percent > 0 ? '+' : ''}${gap.percent}`,
              direction: gap.percent < 0 ? t('benchmarkBelow') : t('benchmarkAbove'),
              period: gap.period,
            })
          : t('benchmarkNoGap')}
      </figcaption>

      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        data-dir={dir}
        aria-label={buckets
          .map((b) =>
            t('benchmarkBucketAria', {
              period: b.key,
              kind: b.kind === 'closed' ? t('kindVerifiedSale') : t('kindAsking'),
              count: b.count,
              median: formatMoney(b.median, currency),
            }),
          )
          .join('. ')}
        className="block w-full"
      >
        {[lo, (lo + hi) / 2, hi].map((value) => (
          <text
            key={value}
            x={dir === 'rtl' ? geom.x1 + 8 : geom.x0 - 8}
            y={y(value) + 3}
            textAnchor={dir === 'rtl' ? 'start' : 'end'}
            className="fill-[var(--lc-text-muted)]"
            style={{ font: 'var(--lc-type-data-sm)' }}
          >
            {formatMoney(value, currency)}
          </text>
        ))}

        {periods.map((period, index) => {
          const centre = slotX(index)
          return (
            <g key={period.key}>
              <text
                x={centre}
                y={BOTTOM + 20}
                textAnchor="middle"
                className="fill-[var(--lc-text-muted)]"
                style={{ font: 'var(--lc-type-caption)' }}
              >
                {period.key}
              </text>
              {(['active', 'closed'] as const).map((kind, side) => {
                const bucket = buckets.find((b) => b.key === period.key && b.kind === kind)
                if (!bucket) return null
                // Asking before sold along the reading direction, every period,
                // so the eye can track one series across the chart.
                const offset = (side === 0 ? -1 : 1) * boxWidth * 0.62
                return (
                  <BucketMark
                    key={kind}
                    bucket={bucket}
                    x={centre + (dir === 'rtl' ? -offset : offset)}
                    width={boxWidth}
                    y={y}
                    currency={currency}
                    label={kind === 'closed' ? t('kindVerifiedSale') : t('kindAsking')}
                  />
                )
              })}
            </g>
          )
        })}
      </svg>

      <p className="m-0 mt-[var(--lc-space-2xs)] text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">
        {t('benchmarkBasis', { min: MIN_FOR_BOX })}
      </p>
    </figure>
  )
}

/** One period of one series. Filled = verified sale, hollow = asking. */
function BucketMark({
  bucket,
  x,
  width,
  y,
  currency,
  label,
}: {
  bucket: Bucket
  x: number
  width: number
  y: (value: number) => number
  currency: string
  label: string
}) {
  const sold = bucket.kind === 'closed'
  const stroke = sold ? 'var(--lc-accent-bold-edge)' : 'var(--lc-accent)'
  const half = width / 2
  const title = (
    <title>{`${bucket.key} — ${label}: ${bucket.count} · ${formatMoney(bucket.median, currency)}`}</title>
  )

  if (!bucket.drawBox) {
    return (
      <g data-testid={`benchmark-points-${bucket.kind}-${bucket.key}`}>
        {title}
        {bucket.values.map((value, index) => (
          <circle
            key={index}
            cx={x}
            cy={y(value)}
            r={3.2}
            fill={sold ? stroke : 'var(--lc-surface)'}
            stroke={stroke}
            strokeWidth="1.25"
          />
        ))}
      </g>
    )
  }

  return (
    <g data-testid={`benchmark-box-${bucket.kind}-${bucket.key}`}>
      {title}
      <line x1={x} y1={y(bucket.high)} x2={x} y2={y(bucket.low)} stroke={stroke} strokeWidth="1" />
      <rect
        x={x - half}
        y={y(bucket.p75)}
        width={width}
        height={Math.max(2, y(bucket.p25) - y(bucket.p75))}
        rx={2}
        fill={sold ? stroke : 'var(--lc-surface)'}
        fillOpacity={sold ? 0.55 : 1}
        stroke={stroke}
        strokeWidth="1"
      />
      <line x1={x - half} y1={y(bucket.median)} x2={x + half} y2={y(bucket.median)} stroke={stroke} strokeWidth="2.5" />
    </g>
  )
}
